import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo, LogoLight } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { api, setSession } from '../lib/api';
import { toast } from 'sonner';
import {
  ShieldCheck, Lock, Eye, EyeOff, ArrowLeft, Phone, Mail,
  TrendingUp, Award, Users, ArrowRight,
} from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';

export default function Connexion() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.step === 'done') {
        setSession(data.token, data.user);
        toast.success(t('auth.loginSuccess'));
        if (data.user.role === 'admin') navigate('/admin');
        else navigate('/dashboard');
      } else if (data.step === 'activation') {
        sessionStorage.setItem('bfi_activation_email', email);
        sessionStorage.setItem('bfi_activation_challenge', data.challenge || '');
        navigate('/activation');
      } else if (data.step === 'otp') {
        sessionStorage.setItem('bfi_otp_challenge', data.challenge);
        sessionStorage.setItem('bfi_otp_email', data.masked_email);
        sessionStorage.setItem('bfi_otp_validity', String(data.validity_minutes || 10));
        navigate('/verification-otp');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-white">
      {/* LEFT - Brand panel */}
      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, #0b1538 0%, #1e3a8a 50%, #1e40af 100%)' }}></div>
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-blue-500/25 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] bg-indigo-500/30 rounded-full blur-3xl"></div>
        <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        <div className="relative h-full flex flex-col justify-between p-10 xl:p-14 text-white">
          <Link to="/"><LogoLight size={42} /></Link>

          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-6">
              <ShieldCheck className="w-3 h-3 text-emerald-300"/> {t('auth.secureBankingSpace')}
            </div>
            <h2 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight">
              {t('auth.bankPanelTitle1')}<br/>
              <span className="bg-gradient-to-r from-blue-200 to-indigo-200 bg-clip-text text-transparent">{t('auth.bankPanelTitle2')}</span>
            </h2>
            <p className="text-blue-100/90 mt-5 text-[15px] leading-relaxed">
              {t('auth.bankPanelDesc')}
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4">
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-1 text-emerald-300"><Users className="w-3.5 h-3.5"/></div>
                <div className="text-2xl font-bold mt-1">120k+</div>
                <div className="text-[11px] text-blue-200 uppercase tracking-wider">{t('auth.clientsLabel')}</div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-1 text-emerald-300"><Award className="w-3.5 h-3.5"/></div>
                <div className="text-2xl font-bold mt-1">4,9/5</div>
                <div className="text-[11px] text-blue-200 uppercase tracking-wider">{t('auth.reviewsLabel')}</div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-1 text-emerald-300"><TrendingUp className="w-3.5 h-3.5"/></div>
                <div className="text-2xl font-bold mt-1">SEPA+SWIFT</div>
                <div className="text-[11px] text-blue-200 uppercase tracking-wider">{t('auth.worldwideLabel')}</div>
              </div>
            </div>
          </div>

          <div className="text-xs text-blue-200/70 flex items-center gap-3">
            <ShieldCheck className="w-3.5 h-3.5"/> {t('auth.encryptedConnection')}
          </div>
        </div>
      </div>

      {/* RIGHT - Form */}
      <div className="lg:col-span-3 flex flex-col">
        {/* Topbar */}
        <div className="border-b border-slate-100">
          <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="text-sm text-slate-500 hover:text-blue-800 inline-flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4"/> {t('auth.backHome')}
            </Link>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8"><Logo /></div>

            <div className="text-xs font-semibold text-blue-700 uppercase tracking-widest">{t('auth.clientSpace')}</div>
            <h1 className="text-3xl md:text-4xl font-bold mt-2 tracking-tight">{t('auth.welcome')}</h1>
            <p className="text-slate-600 mt-2">{t('auth.loginSubtitle')}</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">{t('auth.loginIdentifier')}</Label>
                <Input
                  id="email" type="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  placeholder="prenom.nom@email.com" required
                  className="mt-1.5 h-11"
                  data-testid="input-login-email"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label htmlFor="password" className="text-sm font-medium">{t('common.password')}</Label>
                  <Link to="/mot-de-passe-oublie" className="text-xs text-blue-700 hover:underline font-medium">
                    {t('auth.forgotShort')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password" type={showPwd ? 'text' : 'password'} autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" required
                    className="h-11 pr-10"
                    data-testid="input-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label={t('auth.toggleShowPassword')}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} id="remember"/>
                {t('auth.rememberMe')}
              </label>

              <Button
                type="submit"
                className="w-full h-11 bg-blue-700 hover:bg-blue-800 text-base"
                disabled={loading}
                data-testid="btn-login-submit"
              >
                {loading ? t('auth.loginInProgress') : (<>{t('auth.loginAction')} <ArrowRight className="ml-2 w-4 h-4"/></>)}
              </Button>

              <div className="flex items-center gap-3 text-xs text-slate-500">
                <Lock className="w-3.5 h-3.5 text-emerald-600"/>
                {t('auth.protectedConnection')}
              </div>
            </form>

            <div className="mt-10 pt-6 border-t border-slate-100">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-slate-600">
                  {t('auth.notClientYet')}{' '}
                  <Link to="/inscription" className="text-blue-700 font-semibold hover:underline">
                    {t('common.signup')}
                  </Link>
                </div>
                <a href="mailto:contact@bfigroupefr.com" className="text-xs text-slate-500 hover:text-blue-800 inline-flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5"/> contact@bfigroupefr.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:flex border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400 justify-between items-center">
          <div>© {new Date().getFullYear()} BFI Groupe — {t('footer.copyright')}</div>
          <div className="flex gap-4">
            <Link to="/mentions-legales" className="hover:text-slate-700">{t('footer.legalNotice')}</Link>
            <Link to="/confidentialite" className="hover:text-slate-700">{t('footer.privacy')}</Link>
            <Link to="/cookies" className="hover:text-slate-700">{t('footer.cookies')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
